import { useState, useMemo } from "react";
import { Tab, Tabs } from "@mui/material";
import { debounce } from "@mui/material/utils";
import AddIcon from "@mui/icons-material/Add";
import Button from "@commons/Button";
import TextInput from "@commons/Input/Text";
import { usePage } from "@hooks/usePage";
import { mutate } from "swr";
import TableActiveUsers from "../components/TableActiveUsers";
import TableBlockedUsers from "../components/TableBlockedUsers";
import ModalUserForm from "../components/ModalUserForm";
import type { UserItemList } from "@services/users/list/type";

type UserStatus = "active" | "blocked";

export default function UsersScreen() {
  const { openModal } = usePage();
  const [selectedStatus, setSelectedStatus] = useState<UserStatus>("active");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [userToEdit, setUserToEdit] = useState<UserItemList | null>(null);

  const debouncedSetSearch = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedSearch(value);
      }, 300),
    [],
  );

  const handleUserSuccess = () => {
    mutate((key) => Array.isArray(key) && key[0] === "fetch-get-users");
  };

  const renderContent = () => {
    const components = {
      active: (
        <TableActiveUsers
          search={debouncedSearch}
          onEditUser={(user) => {
            setUserToEdit(user);
            openModal("editUser");
          }}
          onSuccess={handleUserSuccess}
        />
      ),
      blocked: (
        <TableBlockedUsers
          search={debouncedSearch}
          onEditUser={(user) => {
            setUserToEdit(user);
            openModal("editUser");
          }}
          onSuccess={handleUserSuccess}
        />
      ),
    };

    return components[selectedStatus];
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="w-full sm:max-w-md">
          <TextInput
            textSize="sm"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              debouncedSetSearch(e.target.value);
            }}
            placeholder="Pesquisar por nome ou e-mail"
            fullWidth
          />
        </div>
        <Button
          startIcon={<AddIcon />}
          customVariant="secondary"
          size="auto"
          onClick={() => {
            setUserToEdit(null);
            openModal("newUser");
          }}
        >
          Criar Novo Usuário
        </Button>
      </div>

      <div>
        <Tabs
          value={selectedStatus}
          onChange={(_, newValue) => setSelectedStatus(newValue)}
          textColor="primary"
          indicatorColor="primary"
          aria-label="tabs de usuários"
          className="ml-4 mt-2"
        >
          <Tab value="active" label="Usuários Ativos" />
          <Tab value="blocked" label="Usuários Bloqueados" />
        </Tabs>

        {renderContent()}
      </div>

      <ModalUserForm user={userToEdit} onSuccess={handleUserSuccess} />
    </div>
  );
}
